type Listener = (...args: any[]) => void;

/**
 * Minimal event emitter replacing `eventemitter3`. Supports the subset we use:
 * on / once / off (alias removeListener) / emit. Listeners for an event are
 * copied before dispatch, so a listener may safely remove itself (or others)
 * during emit.
 */
export class EventEmitter {
  private readonly events = new Map<string | symbol, Listener[]>();

  on(event: string | symbol, fn: Listener): this {
    const listeners = this.events.get(event);
    if (listeners) {
      listeners.push(fn);
    } else {
      this.events.set(event, [fn]);
    }
    return this;
  }

  once(event: string | symbol, fn: Listener): this {
    const wrapper: Listener = (...args) => {
      this.off(event, wrapper);
      fn(...args);
    };
    return this.on(event, wrapper);
  }

  off(event: string | symbol, fn: Listener): this {
    const listeners = this.events.get(event);
    if (listeners) {
      const remaining = listeners.filter((listener) => listener !== fn);
      if (remaining.length) {
        this.events.set(event, remaining);
      } else {
        this.events.delete(event);
      }
    }
    return this;
  }

  removeListener(event: string | symbol, fn: Listener): this {
    return this.off(event, fn);
  }

  emit(event: string | symbol, ...args: any[]): boolean {
    const listeners = this.events.get(event);
    if (!listeners || !listeners.length) {
      return false;
    }
    for (const listener of listeners.slice()) {
      listener(...args);
    }
    return true;
  }
}

export default EventEmitter;
